/**
 * The wizard's distance-unit preselect, moved here from `steps/Step5Pace.tsx`
 * when ruling 3 (2026-10-22) removed the universal pace screen that owned it.
 * The weekly-mileage question in the training-data beat is now the one place
 * the athlete says miles or kilometres, and it starts from this guess.
 *
 * ⚖️ "No unit defaults without a tap; preselect km only from a known location
 * country" — the ratified rule, and the whole reason this list exists.
 *
 * ⛔ READ THE SHAPE OF THE RULE BEFORE EDITING IT. There is exactly ONE
 * automatic preselection and it is `km`, granted only when the country the
 * athlete typed on the location step is one we RECOGNISE. Everything else — an
 * empty country, a typo, a country not on this list, and every miles-first
 * country — falls through to NO SELECTION. That is the fail-safe direction: an
 * unset unit asks a question, a wrong default silently mislabels a distance by a
 * factor of 1.6.
 *
 * ⚠️ This is a UI PRESELECT, nothing more. It is reversed by one tap and it is
 * emphatically NOT the client-side region-to-country inference B58 refused to
 * write — that one would have invented STORED DATA from a guess. This one only
 * decides which of two cells starts lit.
 */
import type { DistanceUnit } from './firstPlan/trainingData';

const KM_COUNTRIES = new Set([
  'argentina', 'australia', 'austria', 'belgium', 'bolivia', 'brasil', 'brazil',
  'canada', 'chile', 'china', 'colombia', 'costa rica', 'croatia', 'czechia',
  'czech republic', 'denmark', 'dominican republic', 'ecuador', 'egypt',
  'el salvador', 'estonia', 'finland', 'france', 'germany', 'greece',
  'guatemala', 'honduras', 'hungary', 'iceland', 'india', 'indonesia',
  'ireland', 'israel', 'italy', 'japan', 'kenya', 'latvia', 'lithuania',
  'luxembourg', 'malaysia', 'mexico', 'morocco', 'netherlands', 'new zealand',
  'nicaragua', 'nigeria', 'norway', 'panama', 'paraguay', 'peru',
  'philippines', 'poland', 'portugal', 'romania', 'singapore', 'slovakia',
  'slovenia', 'south africa', 'south korea', 'spain', 'sweden', 'switzerland',
  'taiwan', 'thailand', 'turkey', 'ukraine', 'uruguay', 'venezuela',
  'vietnam',
]);

/** `'km'` when the typed country is one we recognise, `undefined` otherwise.
 *  ⛔ Never returns `'mi'` — see the header. */
export function preselectUnit(country: string | undefined): DistanceUnit | undefined {
  const key = country?.trim().toLowerCase();
  if (!key) return undefined;
  return KM_COUNTRIES.has(key) ? 'km' : undefined;
}
